import { useState, useEffect } from 'react';
import { api } from '../../lib/api';

interface SystemSettings {
  allow_registration: boolean;
  require_email_verification: boolean;
  default_user_role: string;
  max_login_attempts: number;
  lockout_duration_minutes: number;
  session_timeout_hours: number;
  password_min_length: number;
  require_mfa_for_admins: boolean;
  turnstile_enabled: boolean;
  maintenance_mode: boolean;
  maintenance_message: string;
  default_quota_forms: number;
  default_quota_submissions: number;
  default_quota_storage_mb: number;
}

const defaultSettings: SystemSettings = {
  allow_registration: true,
  require_email_verification: true,
  default_user_role: 'user',
  max_login_attempts: 5,
  lockout_duration_minutes: 15,
  session_timeout_hours: 24,
  password_min_length: 12,
  require_mfa_for_admins: false,
  turnstile_enabled: false,
  maintenance_mode: false,
  maintenance_message: 'The system is currently undergoing maintenance. Please try again later.',
  default_quota_forms: 10,
  default_quota_submissions: 1000,
  default_quota_storage_mb: 100,
};

export default function SettingsPage() {
  const [settings, setSettings] = useState<SystemSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/settings');
      if (response.ok) {
        const data = await response.json() as { settings: SystemSettings };
        setSettings({ ...defaultSettings, ...data.settings });
      } else {
        const data = await response.json() as { error?: string };
        setError(data.error || 'Failed to load settings');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await api.put('/api/admin/settings', settings);
      if (response.ok) {
        setSuccess('Settings saved successfully');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const data = await response.json() as { error?: string };
        setError(data.error || 'Failed to save settings');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = <K extends keyof SystemSettings>(
    key: K,
    value: SystemSettings[K]
  ) => {
    setSettings((s) => ({ ...s, [key]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">System Settings</h1>
        <button
          onClick={saveSettings}
          disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700">
          {success}
        </div>
      )}

      {/* Registration & Authentication */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Registration & Authentication</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">Allow Registration</label>
              <p className="text-xs text-gray-500">Enable new user registration</p>
            </div>
            <button
              onClick={() => updateSetting('allow_registration', !settings.allow_registration)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.allow_registration ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.allow_registration ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">Require Email Verification</label>
              <p className="text-xs text-gray-500">Users must verify email before login</p>
            </div>
            <button
              onClick={() => updateSetting('require_email_verification', !settings.require_email_verification)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.require_email_verification ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.require_email_verification ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default User Role</label>
            <select
              value={settings.default_user_role}
              onChange={(e) => updateSetting('default_user_role', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </div>
      </div>

      {/* Security */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Security</h2>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Login Attempts</label>
              <input
                type="number"
                min="1"
                max="20"
                value={settings.max_login_attempts}
                onChange={(e) => updateSetting('max_login_attempts', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lockout Duration (minutes)</label>
              <input
                type="number"
                min="1"
                max="1440"
                value={settings.lockout_duration_minutes}
                onChange={(e) => updateSetting('lockout_duration_minutes', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Session Timeout (hours)</label>
              <input
                type="number"
                min="1"
                max="720"
                value={settings.session_timeout_hours}
                onChange={(e) => updateSetting('session_timeout_hours', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Password Length</label>
              <input
                type="number"
                min="8"
                max="128"
                value={settings.password_min_length}
                onChange={(e) => updateSetting('password_min_length', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">Require MFA for Admins</label>
              <p className="text-xs text-gray-500">Force MFA for admin and superadmin users</p>
            </div>
            <button
              onClick={() => updateSetting('require_mfa_for_admins', !settings.require_mfa_for_admins)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.require_mfa_for_admins ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.require_mfa_for_admins ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">Turnstile CAPTCHA</label>
              <p className="text-xs text-gray-500">Enable Cloudflare Turnstile on auth pages</p>
            </div>
            <button
              onClick={() => updateSetting('turnstile_enabled', !settings.turnstile_enabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.turnstile_enabled ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.turnstile_enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Default Quotas */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Default User Quotas</h2>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max Forms</label>
            <input
              type="number"
              min="1"
              value={settings.default_quota_forms}
              onChange={(e) => updateSetting('default_quota_forms', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max Submissions/Form</label>
            <input
              type="number"
              min="1"
              value={settings.default_quota_submissions}
              onChange={(e) => updateSetting('default_quota_submissions', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max Storage (MB)</label>
            <input
              type="number"
              min="1"
              value={settings.default_quota_storage_mb}
              onChange={(e) => updateSetting('default_quota_storage_mb', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Maintenance Mode */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Maintenance Mode</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">Enable Maintenance Mode</label>
              <p className="text-xs text-gray-500">Block all non-admin access to the system</p>
            </div>
            <button
              onClick={() => updateSetting('maintenance_mode', !settings.maintenance_mode)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.maintenance_mode ? 'bg-red-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.maintenance_mode ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Maintenance Message</label>
            <textarea
              value={settings.maintenance_message}
              onChange={(e) => updateSetting('maintenance_message', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
